"use client";

import Image from "next/image";
import {
  ArrowRight,
  Building2,
  CalendarCheck,
  Check,
  ChevronRight,
  Clock3,
  Crown,
  GlassWater,
  Music2,
  ShipWheel,
  Sparkles,
  Star,
  Users,
  Waves,
} from "lucide-react";
import { CountUp, MotionDiv, MotionSection, fadeInLeft, fadeInRight } from "@/components/motion";
import { Button } from "@/components/ui/button";

const eventTypes = [
  "Birthday",
  "Bachelorette",
  "Corporate",
  "Anniversary",
  "After-wedding",
  "Sunset cruise",
];

const differentiators = [
  ["30", "Guests", "A real private-event capacity for bigger celebrations."],
  ["Full", "Bar", "Staffed drinks and premium packages — not a cooler-on-board setup."],
  ["VIP", "Setup", "Crew, music atmosphere, catering, and seating handled before you arrive."],
];

const packages = [
  {
    name: "Signature Sunset Social",
    bestFor: "Birthdays, anniversaries, and elevated friend groups",
    image: "/brand/image-2.jpg",
    features: ["Private cruise window", "Cash or hosted bar", "Sunset photo moments", "Crew-led setup"],
  },
  {
    name: "VIP Party Deck",
    bestFor: "Bachelor/bachelorette parties and nightlife-style celebrations",
    image: "/brand/image-1.png",
    features: ["Open bar options", "Dance floor energy", "VIP seating zones", "Music-forward atmosphere"],
    featured: true,
  },
  {
    name: "Corporate Harbor Buyout",
    bestFor: "Team events, client hosting, and private business outings",
    image: "/brand/image-4.jpg",
    features: ["Catering add-ons", "Branded arrival flow", "Conversation seating", "Simple group coordination"],
  },
];

const quizSteps = [
  ["01", "Choose your moment", "Birthday blowout, corporate mixer, after-wedding cruise, or sunset celebration."],
  ["02", "Set the service level", "Select hosted bar, catering, VIP seating, music energy, and timing preferences."],
  ["03", "Get a private plan", "The team confirms availability and shapes a quote around your group."],
];

const testimonials = [
  "The boat felt like a private waterfront lounge — our group never had to think about setup, drinks, or music.",
  "Perfect for a corporate summer event. It felt polished, organized, and much more premium than a standard boat rental.",
  "Our bachelorette party had the sunset, dancing, cocktails, and space for everyone. It felt like the main event of the weekend.",
];

const faqs = [
  ["Is this a BYOB cruise?", "No — the premium positioning is built around a full-service bar experience, with hosted and package options available for private groups."],
  ["How many guests can we bring?", "Private events can host up to 30 guests, making it a stronger fit for larger celebrations than typical small-group cruises."],
  ["Can we add catering?", "Yes. Catering options can be planned with the cruise so your group arrives to a complete, hosted event instead of coordinating vendors on the day."],
  ["What event types work best?", "Birthdays, bachelor and bachelorette parties, anniversaries, corporate outings, after-wedding celebrations, and sunset private cruises are all strong fits."],
];

function SiteHeader() {
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-border/40 bg-background/78 backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-theme items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="#top" className="flex items-center gap-3" aria-label="Tiki Boat Long Island home">
          <span className="relative h-11 w-20 overflow-hidden rounded-full border border-border/50 bg-card/70 p-1.5">
            <Image src="/brand/logo.png" alt="Tiki Boat Long Island logo" fill sizes="80px" className="object-contain p-1" priority />
          </span>
          <span className="hidden text-xs font-bold uppercase tracking-[0.22em] text-muted-foreground sm:block">
            Long Island private events
          </span>
        </a>
        <nav className="hidden items-center gap-7 text-sm font-semibold text-foreground/80 lg:flex" aria-label="Primary navigation">
          <a href="#experiences" className="transition-colors hover:text-primary">Experiences</a>
          <a href="#packages" className="transition-colors hover:text-primary">Packages</a>
          <a href="#quiz" className="transition-colors hover:text-primary">Plan your cruise</a>
          <a href="#proof" className="transition-colors hover:text-primary">Proof</a>
        </nav>
        <Button asChild className="rounded-button bg-primary px-5 text-primary-foreground shadow-theme-glow hover:bg-primary/90">
          <a href="#quiz">Reserve <ArrowRight className="ml-2 h-4 w-4" /></a>
        </Button>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative min-h-screen overflow-hidden bg-background pt-24 text-foreground">
      <Image
        src="/brand/hero.jpg"
        alt="Tiki Boat Long Island cruising at sunset with party guests on the water"
        fill
        priority
        sizes="100vw"
        className="object-cover opacity-72"
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_72%_22%,hsl(var(--primary-glow)/0.34),transparent_32%),linear-gradient(90deg,hsl(var(--background)/0.98)_0%,hsl(var(--background)/0.76)_42%,hsl(var(--background)/0.28)_100%)]" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-background to-transparent" />

      <div className="relative z-10 mx-auto grid min-h-[calc(100svh-6rem)] max-w-theme items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1.03fr_0.97fr] lg:px-8">
        <MotionDiv variants={fadeInLeft}>
          <p className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/58 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-accent shadow-theme-card backdrop-blur">
            <Crown className="h-4 w-4" /> Premium private tiki boat events
          </p>
          <h1 className="mt-7 max-w-4xl text-5xl font-black leading-[0.9] tracking-tight sm:text-7xl lg:text-8xl">
            Long Island&apos;s private party boat with nightclub energy.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground sm:text-xl">
            Full-service bar, catering options, VIP seating, bathrooms, dance floor, waterfront views, and a private crew handling the setup — built for celebrations worth upgrading.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="rounded-button bg-primary px-7 text-primary-foreground shadow-theme-glow hover:bg-primary/90">
              <a href="#quiz">Reserve Your Private Cruise <ArrowRight className="ml-2 h-5 w-5" /></a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-button border-border/70 bg-card/48 px-7 text-foreground backdrop-blur hover:bg-card/70">
              <a href="#packages">Explore Party Packages</a>
            </Button>
          </div>
          <div className="mt-10 grid max-w-2xl grid-cols-3 gap-3">
            {differentiators.map(([value, label, copy]) => (
              <article key={label} className="rounded-card border border-border/50 bg-card/48 p-4 shadow-theme-card backdrop-blur">
                <p className="text-3xl font-black text-primary">{value}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-foreground">{label}</p>
                <p className="mt-3 hidden text-sm leading-6 text-muted-foreground sm:block">{copy}</p>
              </article>
            ))}
          </div>
        </MotionDiv>

        <MotionDiv variants={fadeInRight} className="relative hidden lg:block">
          <div className="relative ml-auto aspect-[4/5] max-w-[32rem] overflow-hidden rounded-panel border border-border/60 bg-card/40 shadow-theme-elegant">
            <Image src="/brand/image-3.jpg" alt="Guests celebrating aboard the Tiki Boat Long Island party deck" fill sizes="40vw" className="object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/84 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 rounded-card border border-border/50 bg-background/72 p-5 backdrop-blur-xl">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent">The upgrade signal</p>
              <p className="mt-2 text-2xl font-black">No cooler. No guesswork. Just a hosted private party on the water.</p>
            </div>
          </div>
        </MotionDiv>
      </div>
    </section>
  );
}

function ExperienceFilters() {
  return (
    <MotionSection id="experiences" className="bg-background px-4 py-section sm:px-6 lg:px-8">
      <div className="mx-auto max-w-theme">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <MotionDiv>
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-primary">Event-type filtering</p>
            <h2 className="mt-4 text-4xl font-black leading-tight sm:text-6xl">Pick the vibe. We shape the cruise around it.</h2>
          </MotionDiv>
          <MotionDiv>
            <p className="text-lg leading-8 text-muted-foreground">Every group arrives with a different reason to celebrate. The homepage funnel makes it easy to self-select the right experience before asking for a booking.</p>
          </MotionDiv>
        </div>
        <div className="mt-12 flex flex-wrap gap-3">
          {eventTypes.map((type) => (
            <a key={type} href="#quiz" className="group rounded-full border border-border bg-card px-5 py-3 text-sm font-bold text-foreground shadow-theme-card transition-all hover:-translate-y-1 hover:border-primary hover:text-primary">
              {type} <ChevronRight className="ml-1 inline h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          ))}
        </div>
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {[
            [GlassWater, "Full-service bar", "Hosted and open-bar options move the brand away from low-budget BYOB positioning."],
            [Music2, "Nightlife atmosphere", "Dance floor energy, music, sunset light, and tropical detail create a true party venue."],
            [Users, "Large private groups", "Up to 30 guests with seating, bathrooms, and crew support for larger celebrations."],
          ].map(([Icon, title, copy]) => (
            <MotionDiv key={title as string} className="rounded-panel border border-border bg-card p-7 shadow-theme-card transition-transform hover:-translate-y-1">
              <Icon className="h-8 w-8 text-primary" />
              <h3 className="mt-8 text-2xl font-black">{title as string}</h3>
              <p className="mt-4 leading-7 text-muted-foreground">{copy as string}</p>
            </MotionDiv>
          ))}
        </div>
      </div>
    </MotionSection>
  );
}

function PackageCards() {
  return (
    <section id="packages" className="bg-muted px-4 py-section sm:px-6 lg:px-8">
      <div className="mx-auto max-w-theme">
        <div className="max-w-3xl">
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-primary">Visual package comparison</p>
          <h2 className="mt-4 text-4xl font-black leading-tight sm:text-6xl">Packages that feel like experiences, not line items.</h2>
        </div>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {packages.map((pkg) => (
            <article key={pkg.name} className={`group overflow-hidden rounded-panel border bg-card shadow-theme-card transition-all hover:-translate-y-2 ${pkg.featured ? "border-primary shadow-theme-glow" : "border-border"}`}>
              <div className="relative h-72 overflow-hidden">
                <Image src={pkg.image} alt={`${pkg.name} event experience aboard Tiki Boat Long Island`} fill sizes="(min-width: 1024px) 33vw, 100vw" className="object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                {pkg.featured && <span className="absolute left-5 top-5 rounded-full bg-primary px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-primary-foreground">Most premium</span>}
              </div>
              <div className="p-6">
                <p className="text-sm font-semibold text-accent">{pkg.bestFor}</p>
                <h3 className="mt-3 text-3xl font-black">{pkg.name}</h3>
                <ul className="mt-6 space-y-3">
                  {pkg.features.map((feature) => (
                    <li key={feature} className="flex gap-3 text-sm font-semibold text-muted-foreground">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {feature}
                    </li>
                  ))}
                </ul>
                <a href="#quiz" className="mt-7 inline-flex items-center text-sm font-black uppercase tracking-[0.14em] text-primary">Plan this cruise <ArrowRight className="ml-2 h-4 w-4" /></a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function BookingQuiz() {
  return (
    <section id="quiz" className="relative overflow-hidden bg-background px-4 py-section sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,hsl(var(--primary)/0.16),transparent_30%),radial-gradient(circle_at_80%_10%,hsl(var(--primary-glow)/0.14),transparent_32%)]" />
      <div className="relative mx-auto grid max-w-theme gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-primary">Find your perfect cruise</p>
          <h2 className="mt-4 text-4xl font-black leading-tight sm:text-6xl">A concierge-style booking flow for higher-value private events.</h2>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">Instead of forcing guests through a generic form, this flow frames the cruise as a personalized event plan — the right move for private parties, corporate groups, and open-bar packages.</p>
          <Button asChild size="lg" className="mt-8 rounded-button bg-primary px-7 text-primary-foreground shadow-theme-glow hover:bg-primary/90">
            <a href="mailto:info@tikiboatlongisland.com?subject=Private%20Tiki%20Boat%20Cruise%20Request">Start My Private Event Plan <CalendarCheck className="ml-2 h-5 w-5" /></a>
          </Button>
        </div>
        <div className="rounded-panel border border-border bg-card p-4 shadow-theme-elegant sm:p-6">
          {quizSteps.map(([number, title, copy], index) => (
            <div key={title} className="grid gap-4 border-b border-border py-6 last:border-b-0 sm:grid-cols-[5rem_1fr]">
              <p className="text-4xl font-black text-primary">{number}</p>
              <div>
                <h3 className="text-2xl font-black">{title}</h3>
                <p className="mt-2 leading-7 text-muted-foreground">{copy}</p>
                {index === 1 && (
                  <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-bold uppercase tracking-[0.12em] text-foreground sm:grid-cols-3">
                    {["Open bar", "Catering", "VIP seats", "Dance floor", "Sunset", "Crew setup"].map((item) => (
                      <span key={item} className="rounded-full border border-border bg-muted px-3 py-2 text-center">{item}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProofSection() {
  return (
    <section id="proof" className="bg-muted px-4 py-section sm:px-6 lg:px-8">
      <div className="mx-auto max-w-theme">
        <div className="grid gap-8 lg:grid-cols-[0.78fr_1.22fr] lg:items-start">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-primary">Social proof</p>
            <h2 className="mt-4 text-4xl font-black leading-tight sm:text-6xl">Built for groups that want the night to feel handled.</h2>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {[[30, "guest capacity"], [3, "event tiers"], [1, "private crew"]].map(([value, label]) => (
                <div key={label as string} className="rounded-card border border-border bg-card p-4 text-center shadow-theme-card">
                  <p className="text-4xl font-black text-primary"><CountUp target={value as number} suffix={value === 30 ? "+" : ""} /></p>
                  <p className="mt-2 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">{label as string}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {testimonials.map((quote) => (
              <article key={quote} className="rounded-panel border border-border bg-card p-6 shadow-theme-card">
                <div className="flex gap-1 text-primary" aria-label="Five star review">
                  {[1, 2, 3, 4, 5].map((star) => <Star key={star} className="h-4 w-4 fill-current" />)}
                </div>
                <p className="mt-7 text-lg font-semibold leading-8">“{quote}”</p>
              </article>
            ))}
          </div>
        </div>
        <div className="mt-14 rounded-panel border border-border bg-card p-6 shadow-theme-card">
          <p className="text-center text-xs font-black uppercase tracking-[0.22em] text-muted-foreground">Corporate-ready for team outings, client hosting, hospitality groups, venues, and event planners</p>
          <div className="mt-6 grid grid-cols-2 gap-3 text-center text-sm font-black uppercase tracking-[0.16em] text-foreground/70 sm:grid-cols-4">
            {["Event planners", "Hospitality teams", "Corporate groups", "Wedding parties"].map((logo) => <span key={logo} className="rounded-full border border-border bg-muted px-4 py-3">{logo}</span>)}
          </div>
        </div>
      </div>
    </section>
  );
}

function GalleryBand() {
  return (
    <section className="grid bg-background lg:grid-cols-3">
      {[
        ["/brand/image-7.png", "Tiki Boat Long Island party deck with guests and tropical decor"],
        ["/brand/image-8.jpg", "Long Island waterfront party cruise atmosphere"],
        ["/brand/image-10.png", "Tropical tiki boat experience with sunset party energy"],
      ].map(([src, alt]) => (
        <div key={src} className="relative min-h-[22rem] overflow-hidden border-y border-border lg:min-h-[34rem] lg:border-x">
          <Image src={src} alt={alt} fill sizes="(min-width: 1024px) 33vw, 100vw" className="object-cover transition-transform duration-700 hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/58 to-transparent" />
        </div>
      ))}
    </section>
  );
}

function FAQAndCTA() {
  return (
    <section className="bg-background px-4 py-section sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-theme gap-10 lg:grid-cols-[0.95fr_1.05fr]">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-primary">Objection handling</p>
          <h2 className="mt-4 text-4xl font-black leading-tight sm:text-6xl">Premium questions answered before the quote.</h2>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">Clear answers reduce bargain-shopping friction and guide qualified groups toward private, all-inclusive packages.</p>
        </div>
        <div className="space-y-4">
          {faqs.map(([q, a]) => (
            <details key={q} className="group rounded-card border border-border bg-card p-5 shadow-theme-card">
              <summary className="cursor-pointer list-none text-lg font-black marker:hidden">{q}</summary>
              <p className="mt-4 leading-7 text-muted-foreground">{a}</p>
            </details>
          ))}
        </div>
      </div>
      <div className="mx-auto mt-16 max-w-theme overflow-hidden rounded-panel border border-border bg-card shadow-theme-elegant">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
          <div className="p-8 sm:p-12">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-primary">Ready when your group is</p>
            <h2 className="mt-4 text-4xl font-black leading-tight sm:text-6xl">Make the water the venue — and make it feel effortless.</h2>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="rounded-button bg-primary px-7 text-primary-foreground shadow-theme-glow hover:bg-primary/90">
                <a href="#quiz">Reserve Your Private Cruise</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-button border-border px-7">
                <a href="#packages">Explore Party Packages</a>
              </Button>
            </div>
          </div>
          <div className="relative min-h-[24rem]">
            <Image src="/brand/image-5.webp" alt="Tiki boat cruising on Long Island waterfront" fill sizes="(min-width: 1024px) 45vw, 100vw" className="object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-border bg-muted px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-theme flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="relative h-12 w-24 overflow-hidden rounded-full border border-border bg-card p-2">
            <Image src="/brand/logo.png" alt="Tiki Boat Long Island logo" fill sizes="96px" className="object-contain p-1" />
          </span>
          <p className="max-w-sm text-sm font-semibold text-muted-foreground">Premium private party boat experiences for Long Island celebrations, corporate outings, and waterfront nightlife.</p>
        </div>
        <div className="flex flex-wrap gap-4 text-sm font-bold text-foreground/80">
          <a href="#experiences" className="hover:text-primary">Experiences</a>
          <a href="#packages" className="hover:text-primary">Packages</a>
          <a href="#quiz" className="hover:text-primary">Book</a>
        </div>
      </div>
    </footer>
  );
}

export default function TikiBoatLuxuryHome() {
  return (
    <main className="min-h-screen overflow-x-clip bg-background text-foreground">
      <SiteHeader />
      <Hero />
      <ExperienceFilters />
      <PackageCards />
      <BookingQuiz />
      <ProofSection />
      <GalleryBand />
      <FAQAndCTA />
      <SiteFooter />
      <a href="#quiz" className="fixed bottom-4 left-4 right-4 z-50 flex h-14 items-center justify-center gap-2 rounded-button bg-primary text-sm font-black uppercase tracking-[0.14em] text-primary-foreground shadow-theme-glow lg:hidden">
        Reserve Your Cruise <ShipWheel className="h-4 w-4" />
      </a>
    </main>
  );
}
